//~ Define your core data model interfaces for this feature

import { SupportedLocaleCode } from '@constants/locales';

const TITLE_TYPES = ['Principal'] as const;
export type TitleType = (typeof TITLE_TYPES)[number];
export type Titles = Record<TitleType, string>;
type Localized<T> = Record<SupportedLocaleCode, T>;

export type LocalizedTitles = Localized<Titles>;

export const isTitleType = (type: string): type is TitleType => {
    return TITLE_TYPES.includes(type as TitleType);
};

export const CREDIT_ROLES = [
    'Actor',
    'Director',
    'Music',
    'WriterScreenPlay',
    'EditorialOffice',
] as const;
export type CreditRole = (typeof CREDIT_ROLES)[number];
export type Credits = Record<CreditRole, string[]>;

export const isCreditRole = (role: string): role is CreditRole => {
    return CREDIT_ROLES.includes(role as CreditRole);
};
export type Genre = string[];
export type LocalizedGenres = Localized<Genre>;

export const SYNOPSIS_TYPES = ['Short', 'Full'] as const;
export type SynopsisType = (typeof SYNOPSIS_TYPES)[number];
export type Synopses = Record<SynopsisType, string>;
export type LocalizedSynopses = Localized<Synopses>;

export const isSynopsisType = (type: string): type is SynopsisType => {
    return SYNOPSIS_TYPES.includes(type as SynopsisType);
};

interface Size {
    height: number;
    width: number;
}

export const IMAGE_TYPES = [
    'background',
    'cover',
    'landscape',
    'low',
    'medium',
    'high',
] as const;

export type ImageType = (typeof IMAGE_TYPES)[number];

type ImageTag = 'no-text';
export interface Image {
    size: Size;
    url: string;
    // tags?: ImageTag[];
}
export type Images = Record<ImageType, Image>;

export const isImageType = (type: string): type is ImageType => {
    return IMAGE_TYPES.includes(type as ImageType);
};

export interface FrontendCollectionMetadata {
    proscopeId: string;
    encodedTitle: string;
    titles: LocalizedTitles;
    collectionId: string;

    duration: string;
    releaseDate: string;
    productionCountry: string;
    credits: Credits;
    genres: LocalizedGenres;
    synopses: LocalizedSynopses;
    images: Images;

    trailers: string[];
}

export interface CollectionMetadata {
    proscopeId: string;
    title: string;
    collectionId: string;

    duration: string;
    releaseDate: string;
    productionCountry: string;
    credits: Credits;
    genres: Genre;
    synopses: Synopses;
    images: Images;

    trailers: string[];
}

//~ Define Backend response types if they differ significantly from your frontend model
export interface BackendCollectionMetadata {
    duration: string;
    productionCountry: string;
    titles: Array<{
        type: string; //? create real types type
        lang: string;
        value: string;
    }>;
    credits: Array<{
        id: string;
        name: string;
        role: 'Actor' | 'Director' | 'Music' | 'WriterScreenPlay';
    }>;
    genres: {
        lang: string;
        values: string[];
    };
    synopses: Array<{
        lang: string;
        type: SynopsisType; //? create real types type
        value: string;
    }>;
    images: Array<{
        id: string;
        size: Size;
        url: string;
        tags?: ImageTag[]; //? create real tags type
    }>;
    trailerUrl?: {
        player: string;
        start: {
            method: string;
            params: Array<{
                name: string;
                value: {
                    op: string;
                    args: Array<{
                        path: string;
                        type: string;
                    }>;
                };
            }>;
        };

        uri: string;
    };
    releaseDate: string;
}

export const DEFAULT_CREDITS: Credits = {
    Actor: [],
    Director: [],
    Music: [],
    WriterScreenPlay: [],
    EditorialOffice: [],
};

export const DEFAULT_SYNOPSIS: Synopses = {
    Short: '',
    Full: '',
};

export const DEFAULT_LOCALIZED_SYNOPSES: LocalizedSynopses = {
    en: DEFAULT_SYNOPSIS,
    fr: DEFAULT_SYNOPSIS,
};

export const DEFAULT_SIZE: Size = {
    height: 0,
    width: 0,
};

export const DEFAULT_IMAGE: Image = {
    size: DEFAULT_SIZE,
    url: '',
};

export const DEFAULT_IMAGES: Images = {
    background: DEFAULT_IMAGE,
    cover: DEFAULT_IMAGE,
    landscape: DEFAULT_IMAGE,
    low: DEFAULT_IMAGE,
    medium: DEFAULT_IMAGE,
    high: DEFAULT_IMAGE,
};

export const DEFAULT_LOCALIZED_METADATA: CollectionMetadata = {
    proscopeId: '',
    title: '',
    collectionId: '',
    duration: '',
    releaseDate: '',
    productionCountry: '',
    credits: DEFAULT_CREDITS,
    genres: [],
    synopses: DEFAULT_SYNOPSIS,
    images: DEFAULT_IMAGES,
    trailers: [],
};

export const DEFAULT_TITLE: Titles = {
    Principal: '',
};

export const DEFAULT_LOCALIZED_TITLES: LocalizedTitles = {
    en: DEFAULT_TITLE,
    fr: DEFAULT_TITLE,
};

export const DEFAULT_LOCALIZED_GENRES: LocalizedGenres = {
    en: [],
    fr: [],
};

export const DEFAULT_COLLECTION_METADATA: FrontendCollectionMetadata = {
    proscopeId: '',
    encodedTitle: '',
    titles: DEFAULT_LOCALIZED_TITLES,
    collectionId: '',
    duration: '',
    releaseDate: '',
    productionCountry: '',
    credits: DEFAULT_CREDITS,
    genres: DEFAULT_LOCALIZED_GENRES,
    synopses: DEFAULT_LOCALIZED_SYNOPSES,
    images: DEFAULT_IMAGES,
    trailers: [],
};

//~ Define specific query arguments if not in the API file direclty
export interface GetCollectionMetadataArgs {
    title: string;
    proscopeId: string;
    collectionId: string;
    locale: SupportedLocaleCode;
}

//~ Define the state shape for your Collection feature's local UI state
